/**
 * Version history of a chart, `DIALOG.versions` (W2-10 projects-data; DESIGN.md §4.21, §5.13; ARCHITECTURE.md §10).
 *
 *  - Automatic versions (every 10 minutes of editing, before imports, exports, restores and large generator runs),
 *    named checkpoints (Ctrl+S, or "Create checkpoint" here with an optional name) and exports, newest first, with
 *    their sizes; the chart's version budget (min(200 MB, 10 % of the quota)) and the retention rules.
 *  - **Restore**: the current state is versioned first ('Before restore'), then the version becomes the document in
 *    ONE undoable step (Ctrl+Z brings the work back). A chart that is not open is opened first.
 *  - **Open as copy**: the version becomes a new chart ("<name> (<date>)") that opens; the original is unchanged.
 *  - **Name**: the version that is the chart's current state can be named (or renamed) in place: persistence
 *    checkpoints again at the same seq, the records merge and the new label wins (an automatic version becomes a
 *    checkpoint, so it is kept). "Name it" after Ctrl+S opens the dialog with that checkpoint's name field ready
 *    (naming.ts); when the chart changed in between, the dialog says so and offers a new named checkpoint instead.
 *  - A chart open in another tab offers "Switch to it" or "Edit here instead" (D§5.13), like the Library.
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import type { DialogProps } from '../../app/registry/types';
import type { AssetMeta, AssetRole, ChartDoc, ProjectId } from '../../core/model/types';
import { newRpeId } from '../../core/util/ids';
import type { ProjectSummary, VersionBudget, VersionInfo } from '../../persist/types';
import type { DocStore } from '../../state/types';
import { Button, Dialog, EmptyState, Icon, pendingEditProps, ProgressBar } from '../../ui';
import { m } from './history.messages';
import {
  formatBytes,
  formatDateTime,
  isKept,
  relativeTime,
  restoreVersion,
  shortcutText,
  sortVersions,
  VERSION_KIND_ICONS,
  VERSION_KIND_TITLES,
} from './historyModel';
import { useCommitVersion, useNow, useServicesStore, useText, type HistoryText } from './hooks';
import { clearNamingRequest, namingRequest } from './naming';
import { requestTabFocus } from './otherTab';
import s from './History.module.css';

interface Listing {
  readonly versions: readonly VersionInfo[];
  readonly budget: VersionBudget;
}

function errorText(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'object' && err !== null) {
    const msg = (err as { readonly message?: unknown }).message;
    if (typeof msg === 'string') return msg;
  }
  return String(err);
}

function errorCode(err: unknown): string | null {
  if (typeof err !== 'object' || err === null) return null;
  const code = (err as { readonly code?: unknown }).code;
  return typeof code === 'string' ? code : null;
}

/** Name of a version: its label, else its kind ("Automatic version", "Checkpoint", ...). */
export function versionTitle(text: HistoryText, v: VersionInfo): string {
  const label = v.label?.trim() ?? '';
  return label === '' ? text.t(VERSION_KIND_TITLES[v.kind]) : label;
}

/** In-place name field of a version (Enter saves, Escape cancels without closing the dialog). */
function NameEditor(props: {
  readonly text: HistoryText;
  readonly initial: string;
  readonly justMade: boolean;
  readonly busy: boolean;
  readonly onSave: (label: string) => void;
  readonly onCancel: () => void;
}) {
  const { text } = props;
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState(props.initial);
  const label = value.trim();
  useEffect(() => {
    input.current?.focus();
    input.current?.select();
  }, []);
  return (
    <form
      className={s.nameForm}
      onSubmit={(e) => {
        e.preventDefault();
        if (!props.busy && label !== '') props.onSave(label);
      }}
    >
      <label className={s.nameLabel} htmlFor={id}>
        {text.t(props.justMade ? m.nameJustMade : m.nameField)}
      </label>
      <span className={s.nameRow}>
        <input
          ref={input}
          id={id}
          className={s.input}
          value={value}
          maxLength={120}
          placeholder={text.t(m.checkpointPlaceholder)}
          {...pendingEditProps(true)}
          onChange={(e) => {
            setValue(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key !== 'Escape') return;
            e.preventDefault();
            e.stopPropagation();
            props.onCancel();
          }}
        />
        <Button
          type="submit"
          size="sm"
          variant="primary"
          icon="Check"
          disabledReason={props.busy ? { key: m.working } : label === '' ? { key: m.nameEmpty } : null}
        >
          {text.t(m.nameSave)}
        </Button>
        <Button size="sm" variant="ghost" onClick={props.onCancel}>
          {text.t(m.nameCancel)}
        </Button>
      </span>
    </form>
  );
}

function VersionRow(props: {
  readonly text: HistoryText;
  readonly v: VersionInfo;
  readonly now: number;
  readonly current: boolean;
  readonly busy: boolean;
  /** The version is the current state of the chart open here (it can be named). */
  readonly canName: boolean;
  /** The name field is open: true, or 'justMade' for the checkpoint Ctrl+S just made. */
  readonly naming: boolean | 'justMade';
  readonly onRestore: () => void;
  readonly onCopy: () => void;
  readonly onStartNaming: () => void;
  readonly onCancelNaming: () => void;
  readonly onName: (label: string) => void;
}) {
  const { text, v, now, current } = props;
  const when = relativeTime(v.at, now, text.locale) ?? text.t(m.justNow);
  const absolute = formatDateTime(v.at, text.locale);
  const title = versionTitle(text, v);
  const busy = props.busy ? { key: m.working } : null;
  const named = (v.label?.trim() ?? '') !== '';
  return (
    <li
      className={s.version}
      data-kind={v.kind}
      data-current={current || undefined}
      data-naming={props.naming !== false || undefined}
    >
      <Icon name={VERSION_KIND_ICONS[v.kind]} size={16} />
      <span className={s.versionText}>
        {props.naming === false ? (
          <span className={s.versionTitle}>{title}</span>
        ) : (
          <NameEditor
            text={text}
            initial={named ? title : ''}
            justMade={props.naming === 'justMade'}
            busy={props.busy}
            onSave={props.onName}
            onCancel={props.onCancelNaming}
          />
        )}
        <span className={s.versionMeta}>
          <span className={s.kindChip}>{text.t(VERSION_KIND_TITLES[v.kind])}</span>
          {isKept(v.kind) && (
            <span className={s.keptChip}>
              <Icon name="Pin" size={12} />
              {text.t(m.alwaysKept)}
            </span>
          )}
          <time dateTime={new Date(v.at).toISOString()} title={absolute}>
            {when}
          </time>
          <span>{formatBytes(v.bytes, text)}</span>
          {current && (
            <span className={s.currentChip}>
              <Icon name="Check" size={12} />
              {text.t(m.currentState)}
            </span>
          )}
        </span>
      </span>
      <span className={s.versionActions}>
        {props.canName && props.naming === false && (
          <Button
            size="sm"
            variant="ghost"
            icon="PenLine"
            disabledReason={busy}
            onClick={props.onStartNaming}
            aria-label={text.t(named ? m.renameButtonLabel : m.nameButtonLabel, { name: title, when: absolute })}
          >
            {text.t(named ? m.renameButton : m.nameButton)}
          </Button>
        )}
        <Button
          size="sm"
          icon="RotateCcw"
          disabledReason={busy ?? (current ? { key: m.alreadyCurrent } : null)}
          onClick={props.onRestore}
          aria-label={text.t(m.restoreLabel, { name: title, when: absolute })}
        >
          {text.t(m.restore)}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          icon="CopyPlus"
          disabledReason={busy}
          onClick={props.onCopy}
          aria-label={text.t(m.openCopyLabel, { name: title, when: absolute })}
        >
          {text.t(m.openCopy)}
        </Button>
      </span>
    </li>
  );
}

export function VersionsDialog(props: DialogProps<'versions'>) {
  const { services } = props;
  const close = (result: null): void => {
    props.close(result);
  };
  const { projectId } = props.props;
  const text = useText(services);
  const ids = useId();
  const store = useServicesStore(services);
  // Re-render after edits: the chart's name and the current version follow them.
  useCommitVersion(store);
  const now = useNow();
  const [listing, setListing] = useState<Listing | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [summary, setSummary] = useState<ProjectSummary | null>(null);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState('');
  const openHere = store?.projectId === projectId;
  const topInput = useRef<HTMLInputElement>(null);
  // "Name it" after Ctrl+S: the checkpoint to name (picked up once; StrictMode-safe: read here, cleared below).
  const [request] = useState(() => namingRequest(services.persist, projectId));
  const [naming, setNaming] = useState<number | null>(request?.seq ?? null);
  useEffect(() => {
    clearNamingRequest(services.persist, projectId);
  }, [services.persist, projectId]);

  const [reloads, setReloads] = useState(0);
  const load = useCallback((): void => {
    setReloads((n) => n + 1);
  }, []);

  useEffect(() => {
    let alive = true;
    services.persist.listVersions(projectId).then(
      (l) => {
        if (!alive) return;
        setListing(l);
        setLoadError(null);
      },
      (err: unknown) => {
        if (alive) setLoadError(errorText(err));
      },
    );
    return () => {
      alive = false;
    };
  }, [services.persist, projectId, reloads]);

  useEffect(() => {
    let alive = true;
    services.persist.listProjects(true).then(
      (list) => {
        if (alive) setSummary(list.find((p) => p.id === projectId) ?? null);
      },
      () => undefined,
    );
    return () => {
      alive = false;
    };
  }, [services.persist, projectId]);

  const meta = openHere ? store.doc.meta : null;
  const rawName = meta?.name ?? summary?.name ?? '';
  const rawLevel = (meta?.level ?? summary?.level ?? '').trim();
  const baseName = rawName.trim() === '' ? text.t(m.untitled) : rawName;
  const chartName = rawLevel === '' ? baseName : `${baseName} ${rawLevel}`;

  const versions = useMemo(() => sortVersions(listing?.versions ?? []), [listing]);
  const currentSeq = openHere ? store.seq : null;

  /**
   * Where the "Name it" request stands once the versions are listed: 'inline' (its row shows the name field),
   * 'current' (no version at the current state yet: the checkpoint form names one), 'stale' (the chart changed
   * since: a new named checkpoint instead), 'notOpen' (the chart is not open here).
   */
  const requestState = ((): 'none' | 'inline' | 'current' | 'stale' | 'notOpen' => {
    if (request === null || naming !== request.seq || listing === null) return 'none';
    if (!openHere) return 'notOpen';
    if (request.seq !== currentSeq) return 'stale';
    return versions.some((v) => v.seq === request.seq) ? 'inline' : 'current';
  })();
  useEffect(() => {
    if (requestState === 'stale' || requestState === 'current') topInput.current?.focus();
  }, [requestState]);

  const createCheckpoint = async (): Promise<void> => {
    setBusy(true);
    try {
      const label = name.trim() === '' ? null : name.trim();
      await services.persist.flush();
      await services.persist.checkpoint(projectId, label, 'checkpoint');
      services.ui.toast(
        label === null ? { key: m.checkpointCreated } : { key: m.checkpointNamed, params: { name: label } },
        { kind: 'success' },
      );
      setName('');
      setNaming(null);
      load();
    } catch (err) {
      services.ui.toast({ key: m.actionFailed, params: { reason: errorText(err) } }, { kind: 'error' });
    } finally {
      setBusy(false);
    }
  };

  /** Names the version that is the current state (a checkpoint again at the same seq; the records merge). */
  const nameVersion = async (v: VersionInfo, label: string): Promise<void> => {
    const cur = services.store;
    if (cur?.projectId !== projectId || cur.seq !== v.seq) {
      services.ui.toast({ key: m.nameNoLonger }, { kind: 'info', durationMs: 8000 });
      setNaming(null);
      return;
    }
    setBusy(true);
    try {
      await services.persist.flush();
      await services.persist.checkpoint(projectId, label, 'checkpoint');
      services.ui.toast({ key: m.named, params: { name: label } }, { kind: 'success' });
      setNaming(null);
      load();
    } catch (err) {
      services.ui.toast({ key: m.actionFailed, params: { reason: errorText(err) } }, { kind: 'error' });
    } finally {
      setBusy(false);
    }
  };

  /**
   * The chart is open in another tab: "Switch to it" (that tab is asked to come forward; this dialog closes) or
   * "Edit here instead" (that tab saves and closes the chart, then it opens here). True when it is open here now.
   */
  const handOver = async (): Promise<boolean> => {
    const choice = await services.ui.choose<'switch' | 'here'>({
      title: { key: m.lockedTitle, params: { name: chartName } },
      body: { key: m.lockedBody },
      options: [
        { value: 'switch', label: { key: m.lockedSwitch }, description: { key: m.lockedSwitchHelp } },
        { value: 'here', label: { key: m.lockedHere }, description: { key: m.lockedHereHelp } },
      ],
    });
    if (choice === 'switch') {
      services.ui.toast(
        requestTabFocus(projectId)
          ? { key: m.lockedSwitched, params: { name: chartName } }
          : { key: m.lockedElsewhere, params: { name: chartName } },
        { kind: 'info', durationMs: 10_000 },
      );
      close(null);
      return false;
    }
    if (choice !== 'here') return false;
    if (!(await services.persist.takeOverLock(projectId))) {
      services.ui.toast({ key: m.lockedRefused }, { kind: 'error', durationMs: 10_000 });
      return false;
    }
    await services.nav.openProject(projectId);
    return true;
  };

  /** The project open here (opening it first when needed); null when it cannot be opened. */
  const ensureOpen = async (): Promise<DocStore | null> => {
    const cur = services.store;
    if (cur?.projectId === projectId) return cur;
    try {
      try {
        await services.nav.openProject(projectId);
      } catch (err) {
        if (errorCode(err) !== 'lockedElsewhere') throw err;
        if (!(await handOver())) return null;
      }
    } catch (err) {
      services.ui.toast(
        { key: m.actionFailed, params: { reason: errorText(err) } },
        { kind: 'error', durationMs: 10_000 },
      );
      return null;
    }
    const opened = services.store;
    return opened?.projectId === projectId ? opened : null;
  };

  const restore = async (v: VersionInfo): Promise<void> => {
    setBusy(true);
    try {
      const target = await ensureOpen();
      if (target === null) return;
      const when = formatDateTime(v.at, text.locale);
      const label = `${versionTitle(text, v)}, ${when}`;
      const out = await restoreVersion(services, target, projectId, v.seq, label);
      if (!out.ok) {
        services.ui.toast(out.reason, { kind: 'error' });
        return;
      }
      services.ui.toast(
        { key: m.restored, params: { name: versionTitle(text, v), when } },
        { kind: 'success', undo: true },
      );
      close(null);
    } catch (err) {
      services.ui.toast({ key: m.actionFailed, params: { reason: errorText(err) } }, { kind: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const openAsCopy = async (v: VersionInfo): Promise<void> => {
    setBusy(true);
    let id: ProjectId;
    try {
      const doc = await services.persist.loadVersion(projectId, v.seq);
      const base = doc.meta.name.trim() === '' ? text.t(m.untitled) : doc.meta.name;
      const copyName = text.t(m.copyName, { name: base, when: formatDateTime(v.at, text.locale) });
      // A new chart: never exported (the version's export record belongs to the original), so the Library and the
      // export reminder do not call it exported.
      const copy: ChartDoc = {
        ...doc,
        meta: { ...doc.meta, name: copyName, rpeId: newRpeId(undefined, new Set([doc.meta.rpeId])) },
        editor: { ...doc.editor, lastExport: null },
      };
      // The illustration travels along so the copy gets its Library thumbnail (assets are shared, not copied).
      const assets: Array<{
        readonly id: NonNullable<ChartDoc['meta']['illustration']>;
        readonly bytes: Uint8Array;
        readonly mime: string;
        readonly role: AssetRole;
        readonly meta: AssetMeta;
      }> = [];
      const ill = doc.meta.illustration === null ? undefined : doc.assets.get(doc.meta.illustration);
      if (ill !== undefined && ill.missing !== true) {
        try {
          const bytes = await services.persist.getAsset(ill.id);
          assets.push({ id: ill.id, bytes, mime: ill.mime, role: ill.role, meta: ill.meta ?? {} });
        } catch {
          // Without the illustration bytes the copy simply has no thumbnail.
        }
      }
      id = await services.persist.createProject(copy, assets);
      services.ui.toast({ key: m.copyCreated, params: { name: copyName } }, { kind: 'success' });
    } catch (err) {
      services.ui.toast({ key: m.actionFailed, params: { reason: errorText(err) } }, { kind: 'error' });
      setBusy(false);
      return;
    }
    close(null);
    try {
      await services.nav.openProject(id);
    } catch (err) {
      services.ui.toast({ key: m.actionFailed, params: { reason: errorText(err) } }, { kind: 'error' });
    }
  };

  const checkpointKeys = shortcutText(services.keymap, text.locale, 'project.checkpoint');
  const budget = listing?.budget ?? null;
  const used = budget?.usedBytes ?? 0;
  const total = budget?.budgetBytes ?? 0;

  return (
    <Dialog
      open
      size="lg"
      title={text.t(m.versionsTitle, { name: chartName })}
      description={text.t(m.retention)}
      onClose={() => {
        close(null);
      }}
      footer={
        <Button
          onClick={() => {
            close(null);
          }}
        >
          {text.t(m.close)}
        </Button>
      }
    >
      <div className={s.versions}>
        {openHere && (
          <form
            className={s.checkpointForm}
            onSubmit={(e) => {
              e.preventDefault();
              if (!busy) void createCheckpoint();
            }}
          >
            <label className={s.checkpointLabel} htmlFor={`${ids}-name`}>
              {text.t(m.checkpointName)}
            </label>
            <input
              ref={topInput}
              id={`${ids}-name`}
              className={s.input}
              value={name}
              maxLength={120}
              placeholder={text.t(m.checkpointPlaceholder)}
              data-autofocus
              onChange={(e) => {
                setName(e.target.value);
              }}
            />
            <Button
              type="submit"
              icon="BookmarkPlus"
              variant="primary"
              disabledReason={busy ? { key: m.working } : null}
            >
              {text.t(m.createCheckpoint)}
            </Button>
          </form>
        )}
        {!openHere && <p className={s.muted}>{text.t(m.notOpenNote)}</p>}
        {(requestState === 'stale' || requestState === 'notOpen') && (
          <p className={s.notice} role="status">
            <Icon name="Info" size={16} />
            <span>{text.t(requestState === 'stale' ? m.nameNoLonger : m.nameNotOpen)}</span>
          </p>
        )}

        {budget !== null && total > 0 && (
          <div className={s.budget}>
            <ProgressBar
              value={Math.min(1, used / total)}
              label={{
                key: m.budgetLabel,
                params: { used: formatBytes(used, text), budget: formatBytes(total, text) },
              }}
            />
            <p className={s.muted}>{text.t(m.budgetNote)}</p>
          </div>
        )}

        {listing === null ? (
          loadError === null ? (
            <p className={s.muted} role="status">
              {text.t(m.loading)}
            </p>
          ) : (
            <p className={s.error} role="alert">
              <Icon name="CircleAlert" size={16} />
              <span>{text.t(m.loadFailed, { reason: loadError })}</span>
              <Button size="sm" onClick={load}>
                {text.t(m.retry)}
              </Button>
            </p>
          )
        ) : versions.length === 0 ? (
          <EmptyState
            icon="History"
            title={{ key: m.noVersionsTitle }}
            body={
              checkpointKeys === null
                ? { key: m.noVersionsBodyNoKeys }
                : { key: m.noVersionsBody, params: { keys: checkpointKeys } }
            }
          />
        ) : (
          <ul className={s.versionList} aria-label={text.t(m.versionsListLabel)}>
            {versions.map((v) => (
              <VersionRow
                key={`${String(v.seq)}:${String(v.at)}`}
                text={text}
                v={v}
                now={now}
                current={currentSeq !== null && v.seq === currentSeq}
                busy={busy}
                canName={currentSeq !== null && v.seq === currentSeq}
                naming={
                  naming === v.seq && currentSeq === v.seq
                    ? request?.justMade === true && naming === request.seq
                      ? 'justMade'
                      : true
                    : false
                }
                onRestore={() => {
                  void restore(v);
                }}
                onCopy={() => {
                  void openAsCopy(v);
                }}
                onStartNaming={() => {
                  setNaming(v.seq);
                }}
                onCancelNaming={() => {
                  setNaming(null);
                }}
                onName={(label) => {
                  void nameVersion(v, label);
                }}
              />
            ))}
          </ul>
        )}
      </div>
    </Dialog>
  );
}
